import { z } from 'zod';

// --- Position ---------------------------------------------------------------

export const positionSchema = z.object({
    x: z.number(),
    y: z.number(),
});
export type Position = z.infer<typeof positionSchema>;

// --- The wire item ------------------------------------------------------------
//
// n8n-style item wire (Increment 1): every node's output — and every edge's
// carried input — is an ARRAY of items, each wrapping one JSON row under
// `json`. This is a plain TS type, not part of the saved graph (nodes/edges
// carry only their static config; items only ever exist at run time inside the
// engine + the injected `WorkflowNodeRunner`). Replaces the old flat
// string-concat wire.
export type WorkflowItem = { json: Record<string, unknown> };

// --- Node types -------------------------------------------------------------
// MVP node set:
//   user-question   → text input from user (workflow entry)
//   document-upload → indexed files (stub in MVP, just metadata)
//   ollama-agent    → LLM call against local Ollama
//   output          → terminal node, displays text result
//   trigger-run     → run button source (acts as flow start)

export const nodeKindSchema = z.enum([
    'trigger-run',
    'user-question',
    // `document-upload` is kept in the enum for back-compat so OLD saved
    // workflows still parse, render, and run — it is no longer offered in the
    // palette (WS-B) nor advertised to the AI builder.
    'document-upload',
    // WS-B: read firm ontology records / run a named-agent read function.
    'ontology-source',
    'agent-function',
    'ollama-agent',
    'anthropic-agent',
    // SCRIVENER: draft an email with AI from upstream input + a prompt, then
    // push it to the owner's Outlook as a draft when the workflow runs (WS-…).
    'scrivener',
    // n8n-intuitive transforms (Increment 1): PURE, engine-resident nodes with
    // no ctx / LLM — filter keeps matching items, merge combines two inputs.
    'filter',
    'merge',
    // The reshape toolkit (Increment 2): more PURE, engine-resident transforms.
    'sort',
    'limit',
    'edit-fields',
    'aggregate',
    'output',
]);
export type NodeKind = z.infer<typeof nodeKindSchema>;

// --- Per-kind data schemas --------------------------------------------------

export const userQuestionDataSchema = z.object({
    label: z.string().default('User Question'),
    placeholder: z.string().default('Allow users to input text'),
    value: z.string().default(''),
});
export type UserQuestionData = z.infer<typeof userQuestionDataSchema>;

// A document is stored on disk; the node carries its metadata + stable id
// so the engine can resolve and read content at execution time.
export const documentRefSchema = z.union([
    // Back-compat: bare string (legacy seeds). Treated as a label only.
    z.string(),
    z.object({
        id: z.string(),
        name: z.string(),
        size: z.number().nonnegative().default(0),
        mime: z.string().default('text/plain'),
        excerpt: z.string().default('').optional(),
    }),
]);
export type DocumentRef = z.infer<typeof documentRefSchema>;

export const documentUploadDataSchema = z.object({
    label: z.string().default('Document Upload'),
    description: z.string().default('Index files and search over them'),
    documents: z.array(documentRefSchema).default([]),
    // Max characters of each document content to inject into the LLM prompt.
    maxContextChars: z.number().int().positive().default(4000),
});
export type DocumentUploadData = z.infer<typeof documentUploadDataSchema>;

// An ontology-source node reads firm records through the server's `ctx.read`
// (persona-bounded). The node carries WHICH Object Type to read, an optional
// `where` filter, a row cap, and an optional field projection. The engine never
// touches the ontology itself — it routes the read through the injected runner.
export const ontologySourceDataSchema = z.object({
    label: z.string().default('Data Source'),
    objectTypeKey: z.string(),
    filter: z.record(z.string(), z.unknown()).default({}),
    maxRows: z.number().int().positive().default(50),
    fields: z.array(z.string()).default([]),
});
export type OntologySourceData = z.infer<typeof ontologySourceDataSchema>;

// An agent-function node runs a named, READ-ONLY function declared on an agent
// (e.g. MASON `list_recon_breaks`). The node carries the agent code + function
// key; the server's catalog resolves it and serialises the read to text.
export const agentFunctionDataSchema = z.object({
    label: z.string().default('Agent Function'),
    agentCode: z.string(),
    functionKey: z.string(),
    params: z.record(z.string(), z.unknown()).default({}),
});
export type AgentFunctionData = z.infer<typeof agentFunctionDataSchema>;

export const ollamaAgentDataSchema = z.object({
    label: z.string().default('Ollama Agent'),
    model: z.string().default('qwen3.6:latest'),
    systemPrompt: z.string().default('You are a helpful assistant.'),
    temperature: z.number().min(0).max(2).default(0.7),
    // Where the Ollama server lives. Defaults to the API server's own
    // OLLAMA_URL env (localhost in dev). Set to a tunnel / LAN URL when
    // the backend runs in the cloud and you want it to call your machine.
    endpointUrl: z.string().default(''),
});
export type OllamaAgentData = z.infer<typeof ollamaAgentDataSchema>;

export const anthropicAgentDataSchema = z.object({
    label: z.string().default('Custom Agent'),
    model: z.string().default('claude-sonnet-4-6'),
    systemPrompt: z.string().default('You are a helpful assistant.'),
    maxTokens: z.number().int().positive().default(4096),
});
export type AnthropicAgentData = z.infer<typeof anthropicAgentDataSchema>;

export const scrivenerDataSchema = z.object({
    label: z.string().default('SCRIVENER'),
    // The drafting instructions (tone, structure, sign-off). The upstream node
    // output supplies WHAT the email is about; this supplies HOW to write it.
    prompt: z
        .string()
        .default('Draft a concise, professional client email based on the input below.'),
    // Optional recipient. Blank creates a Outlook draft with no `To:` header — the
    // IM addresses it in Outlook.
    to: z.string().default(''),
});
export type ScrivenerData = z.infer<typeof scrivenerDataSchema>;

export const outputDataSchema = z.object({
    label: z.string().default('Output'),
    description: z.string().default('Output the results of nodes as text'),
    format: z.enum(['text', 'formatted']).default('formatted'),
});
export type OutputData = z.infer<typeof outputDataSchema>;

// A filter node keeps items whose json matches a conjunctive/disjunctive list
// of static conditions. The operator alone determines the comparison family
// (string / number / boolean / date) — there is no separate `type` field.
// Values are STATIC literals in this increment (no expressions).
export const filterOperatorSchema = z.enum([
    // string
    'is',
    'isNot',
    'contains',
    'startsWith',
    'isEmpty',
    'isNotEmpty',
    // number
    'eq',
    'ne',
    'gt',
    'gte',
    'lt',
    'lte',
    'between',
    // boolean
    'isTrue',
    'isFalse',
    // date (ISO strings, compared lexicographically)
    'before',
    'after',
]);
export type FilterOperator = z.infer<typeof filterOperatorSchema>;

export const filterConditionSchema = z.object({
    field: z.string().default(''),
    operator: filterOperatorSchema.default('is'),
    // Unused by isEmpty/isTrue/isFalse. Numbers arrive as `number`; every other
    // operator compares against a string/boolean literal.
    value: z.union([z.string(), z.number(), z.boolean()]).default(''),
    // Upper bound for the number `between` operator only.
    valueTo: z.number().optional(),
});
export type FilterCondition = z.infer<typeof filterConditionSchema>;

export const filterDataSchema = z.object({
    label: z.string().default('Filter'),
    combinator: z.enum(['and', 'or']).default('and'),
    conditions: z.array(filterConditionSchema).default([]),
});
export type FilterData = z.infer<typeof filterDataSchema>;

// A merge node combines its two inputs (`input1` / `input2` edge handles).
export const mergeModeSchema = z.enum(['append', 'combineByField', 'combineByPosition']);
export type MergeMode = z.infer<typeof mergeModeSchema>;

export const mergeOutputTypeSchema = z.enum([
    'keepMatches',
    'keepNonMatches',
    'keepEverything',
    'enrichInput1',
    'enrichInput2',
]);
export type MergeOutputType = z.infer<typeof mergeOutputTypeSchema>;

export const mergeNonMatchesFromSchema = z.enum(['input1', 'input2', 'both']);
export type MergeNonMatchesFrom = z.infer<typeof mergeNonMatchesFromSchema>;

export const mergeDataSchema = z.object({
    label: z.string().default('Merge'),
    mode: mergeModeSchema.default('append'),
    // `combineByField` only: the equality-match field on each side.
    field1: z.string().default(''),
    field2: z.string().default(''),
    // `combineByField` only: which rows to keep.
    outputType: mergeOutputTypeSchema.default('keepMatches'),
    // `combineByField` + `outputType: 'keepNonMatches'` only.
    nonMatchesFrom: mergeNonMatchesFromSchema.default('both'),
});
export type MergeData = z.infer<typeof mergeDataSchema>;

// A sort node orders items by one field, ascending or descending. The
// comparison is numeric-aware: values that parse as numbers on BOTH sides
// compare numerically; otherwise it falls back to a lexicographic string
// compare (mirrors the filter node's date `before`/`after` convention). No
// field configured is a pass-through, matching the filter node's
// not-yet-configured convention.
export const sortDirectionSchema = z.enum(['asc', 'desc']);
export type SortDirection = z.infer<typeof sortDirectionSchema>;

export const sortDataSchema = z.object({
    label: z.string().default('Sort'),
    field: z.string().default(''),
    direction: sortDirectionSchema.default('asc'),
});
export type SortData = z.infer<typeof sortDataSchema>;

// A limit node keeps a slice of the items — the first `maxItems` after
// skipping `offset`.
export const limitDataSchema = z.object({
    label: z.string().default('Limit'),
    maxItems: z.number().int().positive().default(10),
    offset: z.number().int().nonnegative().default(0),
});
export type LimitData = z.infer<typeof limitDataSchema>;

// An edit-fields node reshapes each item's json (n8n "Set"). The steps apply
// in a fixed order per item: `renames` (copy each `from` value to `to`, then
// drop `from`) → `remove` (explicit drop list) → `keepOnly` (non-empty:
// project down to just those field names) → `sets` (assign static
// field=literal values on top, so a set field is always present even when it
// isn't in `keepOnly`). Because renames run first, `keepOnly` / `remove`
// reference the NEW field names. Everything empty is a pass-through.
export const editFieldSetSchema = z.object({
    field: z.string().default(''),
    value: z.union([z.string(), z.number(), z.boolean()]).default(''),
});
export type EditFieldSet = z.infer<typeof editFieldSetSchema>;

// One field-to-field rename: copy `from`'s value to `to`, then drop `from`.
export const editFieldRenameSchema = z.object({
    from: z.string().default(''),
    to: z.string().default(''),
});
export type EditFieldRename = z.infer<typeof editFieldRenameSchema>;

export const editFieldsDataSchema = z.object({
    label: z.string().default('Edit Fields'),
    // Field-to-field renames, applied BEFORE keepOnly/remove (which then
    // reference the new names).
    renames: z.array(editFieldRenameSchema).default([]),
    // Explicit drop list, applied after renames.
    remove: z.array(z.string()).default([]),
    keepOnly: z.array(z.string()).default([]),
    sets: z.array(editFieldSetSchema).default([]),
});
export type EditFieldsData = z.infer<typeof editFieldsDataSchema>;

// An aggregate node summarises items (n8n "Aggregate"/"Summarize"). `groupBy`
// buckets rows by `groupByField` into one item per distinct value (a `count`
// plus an optional `sum` of `sumField`); `summary` collapses every item into
// a single summary item instead.
export const aggregateModeSchema = z.enum(['groupBy', 'summary']);
export type AggregateMode = z.infer<typeof aggregateModeSchema>;

export const aggregateDataSchema = z.object({
    label: z.string().default('Aggregate'),
    mode: aggregateModeSchema.default('groupBy'),
    // `groupBy` only: the field to bucket rows by.
    groupByField: z.string().default(''),
    // Both modes: optional numeric field to sum alongside the count.
    sumField: z.string().default(''),
});
export type AggregateData = z.infer<typeof aggregateDataSchema>;

export const triggerRunDataSchema = z.object({
    label: z.string().default('Run'),
});
export type TriggerRunData = z.infer<typeof triggerRunDataSchema>;

// --- Discriminated node union ----------------------------------------------

export const baseNodeSchema = z.object({
    id: z.string(),
    position: positionSchema,
});

export const workflowNodeSchema = z.discriminatedUnion('kind', [
    baseNodeSchema.extend({ kind: z.literal('trigger-run'), data: triggerRunDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('user-question'), data: userQuestionDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('document-upload'), data: documentUploadDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('ontology-source'), data: ontologySourceDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('agent-function'), data: agentFunctionDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('ollama-agent'), data: ollamaAgentDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('anthropic-agent'), data: anthropicAgentDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('scrivener'), data: scrivenerDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('filter'), data: filterDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('merge'), data: mergeDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('sort'), data: sortDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('limit'), data: limitDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('edit-fields'), data: editFieldsDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('aggregate'), data: aggregateDataSchema }),
    baseNodeSchema.extend({ kind: z.literal('output'), data: outputDataSchema }),
]);
export type WorkflowNode = z.infer<typeof workflowNodeSchema>;

// --- Edges ------------------------------------------------------------------

export const workflowEdgeSchema = z.object({
    id: z.string(),
    source: z.string(),
    target: z.string(),
    // Which input slot on the TARGET node this edge feeds. Absent / 'main' is
    // the single-input default every node but `merge` reads; `merge` reads
    // 'input1' / 'input2'. Multiple edges into the same handle concatenate
    // (Append), in edge order.
    targetHandle: z.string().optional(),
});
export type WorkflowEdge = z.infer<typeof workflowEdgeSchema>;

// --- Workflow ---------------------------------------------------------------

export const workflowSchema = z.object({
    id: z.string(),
    name: z.string(),
    // v1 = the legacy string-wire graphs (accepted for back-compat, no
    // migration — only node/edge CONFIGS are stored, never wire values); v2 =
    // the item-array wire (Increment 1). New saves always write 2.
    version: z.union([z.literal(1), z.literal(2)]).default(2),
    nodes: z.array(workflowNodeSchema),
    edges: z.array(workflowEdgeSchema),
    createdAt: z.string().datetime().optional(),
    updatedAt: z.string().datetime().optional(),
});
export type Workflow = z.infer<typeof workflowSchema>;
